import React, { useState } from 'react';
import { Tv2, Film } from 'lucide-react';
import { getSafeImageUrl } from '../../utils/imageUtils';

interface SeriesPosterProps {
  poster?: string;
  title: string;
  category?: string;
  year?: number | string;
  className?: string;
  aspectRatio?: '2/3' | '16/9' | '1/1';
  size?: 'sm' | 'md' | 'lg' | 'hero';
}

/**
 * Generates an eye-catching, high-contrast branded gradient cover
 * when a series has no poster or when its image URL fails to load.
 */
function getTitleHash(str: string): number {
  let hash = 0;
  for (let i = 0; i < str.length; i++) {
    hash = str.charCodeAt(i) + ((hash << 5) - hash);
  }
  return Math.abs(hash);
}

const GRADIENTS = [
  'from-indigo-900 via-purple-950 to-zinc-950 border-purple-500/30',
  'from-cyan-950 via-blue-950 to-zinc-950 border-cyan-500/30',
  'from-rose-950 via-red-950 to-zinc-950 border-rose-500/30',
  'from-amber-950 via-orange-950 to-zinc-950 border-amber-500/30',
  'from-emerald-950 via-teal-950 to-zinc-950 border-emerald-500/30',
  'from-violet-950 via-fuchsia-950 to-zinc-950 border-violet-500/30',
  'from-blue-950 via-indigo-950 to-zinc-950 border-blue-500/30',
];

const ACCENT_COLORS = [
  'text-purple-400 bg-purple-500/20 border-purple-500/30',
  'text-cyan-400 bg-cyan-500/20 border-cyan-500/30',
  'text-rose-400 bg-rose-500/20 border-rose-500/30',
  'text-amber-400 bg-amber-500/20 border-amber-500/30',
  'text-emerald-400 bg-emerald-500/20 border-emerald-500/30',
  'text-violet-400 bg-violet-500/20 border-violet-500/30',
  'text-blue-400 bg-blue-500/20 border-blue-500/30',
];

export const SeriesPoster: React.FC<SeriesPosterProps> = ({
  poster,
  title,
  category,
  year,
  className = '',
  aspectRatio = '2/3',
  size = 'md',
}) => {
  const [imageError, setImageError] = useState(false);
  const [isLoaded, setIsLoaded] = useState(false);

  React.useEffect(() => {
    setImageError(false);
    setIsLoaded(false);
  }, [poster]);

  const hasValidPoster = Boolean(poster && poster.trim().length > 5 && !imageError);
  const cleanTitle = (title || 'Série').trim();

  const hash = getTitleHash(cleanTitle);
  const gradientClass = GRADIENTS[hash % GRADIENTS.length];
  const accentClass = ACCENT_COLORS[hash % ACCENT_COLORS.length];

  const aspectClass =
    aspectRatio === '2/3'
      ? 'aspect-[2/3]'
      : aspectRatio === '16/9'
      ? 'aspect-video'
      : 'aspect-square';

  return (
    <div
      className={`relative w-full ${aspectClass} rounded-xl overflow-hidden bg-zinc-900 border border-zinc-800 select-none ${className}`}
    >
      {hasValidPoster ? (
        <>
          {!isLoaded && (
            <div className="absolute inset-0 skeleton-shimmer z-0" />
          )}
          <img
            src={getSafeImageUrl(poster)}
            alt={cleanTitle}
            className={`w-full h-full object-cover group-hover:scale-105 transition-all duration-300 relative z-10 ${
              isLoaded ? 'opacity-100' : 'opacity-0'
            }`}
            loading="lazy"
            decoding="async"
            onLoad={() => setIsLoaded(true)}
            onError={() => setImageError(true)}
          />
        </>
      ) : (
        /* Rich Elegant Fallback Poster for Series without Cover */
        <div
          className={`w-full h-full bg-gradient-to-b ${gradientClass} border p-3 flex flex-col justify-between items-center text-center relative overflow-hidden group-hover:scale-105 transition-transform duration-300`}
        >
          {/* Subtle background glow effect */}
          <div className="absolute -top-10 -right-10 w-28 h-28 bg-white/5 rounded-full blur-2xl pointer-events-none" />
          <div className="absolute -bottom-10 -left-10 w-28 h-28 bg-black/40 rounded-full blur-xl pointer-events-none" />

          {/* Top category tag / TV Series badge */}
          <div className="w-full flex items-center justify-between gap-1 z-10">
            <span
              className={`text-[9px] font-black uppercase tracking-wider px-2 py-0.5 rounded-md border ${accentClass} truncate max-w-[85%]`}
            >
              {category || 'SÉRIE'}
            </span>
            <div className="p-1 rounded-md bg-black/50 text-zinc-400">
              <Tv2 className="w-3 h-3" />
            </div>
          </div>

          {/* Center Title & Graphic typography */}
          <div className="my-auto w-full px-1 z-10 flex flex-col items-center">
            <div className="w-10 h-10 rounded-2xl bg-black/40 border border-white/10 flex items-center justify-center mb-2 shadow-inner">
              <Film className="w-5 h-5 text-zinc-300" />
            </div>
            <h4
              className={`font-black text-white leading-tight line-clamp-3 ${
                size === 'sm'
                  ? 'text-[11px]'
                  : size === 'lg' || size === 'hero'
                  ? 'text-base md:text-lg'
                  : 'text-xs md:text-sm'
              }`}
            >
              {cleanTitle}
            </h4>
          </div>

          {/* Bottom badge: Year or Series Indicator */}
          <div className="w-full flex items-center justify-between text-[9px] font-bold text-zinc-400 z-10 pt-1 border-t border-white/5">
            <span className="font-mono">{year || 'HD'}</span>
            <span className="text-zinc-500 font-black tracking-widest text-[8px] uppercase">
              VOD SÉRIES
            </span>
          </div>
        </div>
      )}
    </div>
  );
};
